import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReqSettings as ReqSettingsData } from '@veta-desk/domain';
import { api, ApiError } from '../lib/api';

// Veta Change: rubros de la estimación y parámetros del flujo de requerimientos (admin).

const input = 'h-9 rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';

export function ReqSettings() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-req'], queryFn: () => api<ReqSettingsData>('/admin/req') });
  const [form, setForm] = useState<ReqSettingsData | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { if (query.data && !form) setForm(query.data); }, [query.data, form]);

  const save = useMutation({
    mutationFn: (f: ReqSettingsData) => api<ReqSettingsData>('/admin/req', { method: 'PUT', body: JSON.stringify({
      phases: f.phases, roundingStep: f.roundingStep, managementPctRange: f.managementPctRange,
      uatAutoAcceptBusinessDays: f.uatAutoAcceptBusinessDays, uatReminderBusinessDay: f.uatReminderBusinessDay,
      evolutiveAutoConfirm: f.evolutiveAutoConfirm
    }) }),
    onSuccess: d => { setForm(d); queryClient.setQueryData(['admin-req'], d); setMsg({ ok: true, text: 'Guardado.' }); },
    onError: e => setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'No se pudo guardar.' })
  });

  if (!form) return <p role="status" className="py-6 text-sm text-muted">Cargando…</p>;
  const set = (patch: Partial<ReqSettingsData>) => { setMsg(null); setForm({ ...form, ...patch }); };
  const setPhase = (i: number, patch: Partial<ReqSettingsData['phases'][number]>) =>
    set({ phases: form.phases.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const move = (i: number, d: -1 | 1) => {
    const phases = [...form.phases];
    const j = i + d;
    if (j < 0 || j >= phases.length) return;
    [phases[i], phases[j]] = [phases[j]!, phases[i]!];
    set({ phases });
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`rounded-md px-3 py-2 text-sm ${msg.ok ? 'bg-accent-soft text-accent-dark' : 'bg-[#FBEAEA] text-[#8F1D1D]'}`}>{msg.text}</p>}
      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">Rubros de la estimación</h2>
        <p className="text-sm text-muted">Las filas de la tabla que llena el consultor (columnas Funcional y ABAP). Un rubro no se borra: se desactiva.</p>
        <ul className="flex flex-col gap-2">
          {form.phases.map((p, i) => (
            <li key={p.id || `new-${i}`} className={`flex items-center gap-2 ${p.active ? '' : 'opacity-60'}`}>
              <span className="w-6 text-right font-mono text-xs text-muted">{i + 1}</span>
              <input aria-label={`Rubro ${i + 1}`} className={`${input} flex-1`} value={p.name} onChange={e => setPhase(i, { name: e.target.value })} />
              <button type="button" className="px-1 text-muted hover:text-ink" aria-label="Subir" onClick={() => move(i, -1)}>↑</button>
              <button type="button" className="px-1 text-muted hover:text-ink" aria-label="Bajar" onClick={() => move(i, 1)}>↓</button>
              <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={p.active} onChange={e => setPhase(i, { active: e.target.checked })} />Activo</label>
            </li>
          ))}
        </ul>
        <div><button type="button" className={btnSecondary} onClick={() => set({ phases: [...form.phases, { id: '', name: '', active: true }] })}>+ Agregar rubro</button></div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">Redondeo de cada celda cotizada (h)
          <input type="number" min="0.25" step="0.25" className={input} value={form.roundingStep} onChange={e => set({ roundingStep: Number(e.target.value) })} />
        </label>
        <label className="flex flex-col gap-1 text-sm">Gestión del servicio sugerida (%)
          <span className="flex items-center gap-2">
            <input type="number" min="0" className={`${input} w-20`} value={form.managementPctRange[0]} onChange={e => set({ managementPctRange: [Number(e.target.value), form.managementPctRange[1]] })} />
            a
            <input type="number" min="0" className={`${input} w-20`} value={form.managementPctRange[1]} onChange={e => set({ managementPctRange: [form.managementPctRange[0], Number(e.target.value)] })} />
          </span>
        </label>
        <label className="flex flex-col gap-1 text-sm">UAT: recordatorio al cliente (día hábil)
          <input type="number" min="1" className={input} value={form.uatReminderBusinessDay} onChange={e => set({ uatReminderBusinessDay: Number(e.target.value) })} />
        </label>
        <label className="flex flex-col gap-1 text-sm">UAT: se acepta sola a los (días hábiles)
          <input type="number" min="1" className={input} value={form.uatAutoAcceptBusinessDays} onChange={e => set({ uatAutoAcceptBusinessDays: Number(e.target.value) })} />
        </label>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={form.evolutiveAutoConfirm} onChange={e => set({ evolutiveAutoConfirm: e.target.checked })} />
          Crear el evolutivo en Veta sin esperar la confirmación de un clic
        </label>
      </section>

      <div className="flex gap-2">
        <button type="button" className={btnPrimary} disabled={save.isPending || form.phases.some(p => p.name.trim().length < 2)} onClick={() => save.mutate(form)}>Guardar</button>
        <button type="button" className={btnSecondary} onClick={() => { setForm(query.data ?? null); setMsg(null); }}>Descartar</button>
      </div>
    </div>
  );
}
